import React, { forwardRef, useImperativeHandle } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { format } from 'date-fns';

import EmployeeDisplay from '@/__core/components/EmployeeDisplay';
import DataTableModule from '@/__core/layout/module/DataTableModule';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';

import { InventoryIssueRow } from '../types';

export type IssueListHandle = {
  requestRefresh: () => void;
};

interface IssueListProps {
  rows: InventoryIssueRow[];
  loading?: boolean;
  onSelectRow?: (row: InventoryIssueRow) => void;
  onRefresh?: () => void;
}

const resolveStatusName = (status: unknown): string => {
  if (!status) return '';
  if (typeof status === 'string') return status;
  if (typeof status === 'object' && status !== null) {
    const obj = status as Record<string, unknown>;
    return String(obj.name || obj.code || '');
  }
  return '';
};

const statusColor = (status: unknown): string => {
  if (status && typeof status === 'object' && status !== null) {
    const obj = status as Record<string, unknown>;
    if (obj.color && typeof obj.color === 'string') {
      return obj.color;
    }
  }
  const name = resolveStatusName(status).toLowerCase();
  switch (name) {
    case 'posted': return '#166534';
    case 'ready_to_post':
    case 'ready': return '#9a3412';
    case 'picked':
    case 'inspected': return '#075985';
    case 'cancelled':
    case 'canceled': return '#991b1b';
    case 'draft': return '#854d0e';
    case 'approved': return '#1e40af';
    default: return '#374151';
  }
};

const statusLabel = (status: unknown) => {
  const name = resolveStatusName(status);
  switch (name.toLowerCase()) {
    case 'posted':
      return 'Đã ghi sổ';
    case 'ready_to_post':
    case 'ready':
      return 'Sẵn sàng ghi sổ';
    case 'picked':
      return 'Đã pick';
    case 'inspected':
      return 'Đã kiểm';
    case 'draft':
      return 'Nháp';
    case 'approved':
      return 'Đã duyệt';
    case 'cancelled':
    case 'canceled':
      return 'Đã hủy';
    default:
      return name || '-';
  }
};

const IssueList = forwardRef<IssueListHandle, IssueListProps>(function IssueList(
  { rows, loading = false, onSelectRow, onRefresh },
  ref
) {
  useImperativeHandle(
    ref,
    () => ({
      requestRefresh: () => onRefresh?.(),
    }),
    [onRefresh]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 1,
          height: 220,
        }}
      >
        <Typography variant='body1' color='text.secondary'>
          Không có phiếu xuất kho
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Thử thay đổi bộ lọc hoặc tạo phiếu mới.
        </Typography>
      </Box>
    );
  }

  const columns: Array<{
    id: string;
    label: string;
    width?: string;
    align?: 'left' | 'center' | 'right';
    render?: (_: unknown, row: InventoryIssueRow) => React.ReactNode;
  }> = [
    {
      id: 'code',
      label: 'Mã phiếu',
      width: '140px',
      render: (_: unknown, row: InventoryIssueRow) => (
        <Typography sx={{ ...sharedListStyles.cellText, fontWeight: 600 }}>{row.code}</Typography>
      ),
    },
    {
      id: 'issued_at',
      label: 'Ngày XK',
      width: '150px',
      render: (_: unknown, row: InventoryIssueRow) => (
        <Typography sx={sharedListStyles.cellText}>
          {row.issued_at ? format(new Date(row.issued_at), 'dd/MM/yyyy HH:mm') : '-'}
        </Typography>
      ),
    },
    {
      id: 'name',
      label: 'Nội dung XK',
      width: '200px',
      render: (_: unknown, row: InventoryIssueRow) => (
        <Typography sx={sharedListStyles.cellText}>{row.name || '—'}</Typography>
      ),
    },
    {
      id: 'order',
      label: 'Đơn hàng',
      width: '180px',
      render: (_: unknown, row: InventoryIssueRow) => {
        const orderNo = row.order_no;
        const customerName = row.customer_name || '';
        if (!orderNo && !customerName) {
          return <Typography sx={sharedListStyles.cellText}>—</Typography>;
        }
        const displayText = orderNo && customerName
          ? `${orderNo} : ${customerName}`
          : orderNo || customerName;
        const textColor = row.order_status_color || undefined;
        return (
          <Typography
            sx={{
              ...sharedListStyles.cellText,
              ...(textColor ? { color: textColor } : {}),
            }}
          >
            {displayText}
          </Typography>
        );
      },
    },
    {
      id: 'status',
      label: 'Trạng thái',
      width: '150px',
      render: (_: unknown, row: InventoryIssueRow) => (
        <Typography sx={{ ...sharedListStyles.cellText, fontWeight: 600, color: statusColor(row.status) }}>
          {statusLabel(row.status)}
        </Typography>
      ),
    },
    {
      id: 'created_at',
      label: 'Ngày tạo',
      width: '165px',
      render: (_: unknown, row: InventoryIssueRow) => (
        <Typography sx={sharedListStyles.cellText}>
          {row.created_at ? format(new Date(row.created_at), 'dd/MM/yyyy HH:mm') : '-'}
        </Typography>
      ),
    },
    {
      id: 'created_by_name',
      label: 'Người tạo',
      width: '190px',
      render: (_: unknown, row: InventoryIssueRow) => (
        <EmployeeDisplay
          name={row.created_by_name}
          userId={row.created_by}
          fallbackText='—'
        />
      ),
    },
    {
      id: 'updated_at',
      label: 'Ngày cập nhật',
      width: '165px',
      render: (_: unknown, row: InventoryIssueRow) => (
        <Typography sx={sharedListStyles.cellText}>
          {row.updated_at ? format(new Date(row.updated_at), 'dd/MM/yyyy HH:mm') : '-'}
        </Typography>
      ),
    },
    {
      id: 'updated_by_name',
      label: 'Người cập nhật',
      width: '190px',
      render: (_: unknown, row: InventoryIssueRow) => (
        <EmployeeDisplay
          name={row.updated_by_name}
          userId={row.updated_by}
          fallbackText='—'
        />
      ),
    },
  ];

  return (
    <Box sx={sharedListStyles.container}>
      <DataTableModule
        data={rows}
        columns={columns}
        leftFilterWidth={0}
        onRowSelect={() => undefined}
        onSelectAll={() => undefined}
        onRowClick={(row) => onSelectRow?.(row as InventoryIssueRow)}
        getRowId={(row) => (row as InventoryIssueRow).id}
        isRowSelected={() => false}
        areAllRowsSelected={false}
        areSomeRowsSelected={false}
        showCheckbox={false}
      />
    </Box>
  );
});

export default IssueList;
